import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Badge, Group, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getSubject, listOrganizations, searchSubjects } from '../../api/resources';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { SubjectDetailPanel } from '../../components/detail/SubjectDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { SUBJECT_TYPE_VALUES } from '../../types/domain';
import { ResourceBrowsePage } from './ResourceBrowsePage';

export function SubjectsPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const detailSubjectId = searchParams.get('id');

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const orgOptions = (orgsQ.data?.data ?? []).map((org) => ({
        value: org.id,
        label: org.name,
    }));

    const extraListParams = useMemo(
        () => ({
            ...(orgFilter ? { organizationId: orgFilter } : {}),
            ...(typeFilter ? { subjectType: typeFilter } : {}),
        }),
        [orgFilter, typeFilter],
    );

    return (
        <>
            <ResourceBrowsePage
                title={t('features.subjects.title')}
                description={t('features.subjects.description')}
                queryKey="subjects-search"
                fetchList={searchSubjects}
                fetchOne={getSubject}
                searchMode="search"
                extraListParams={extraListParams}
                filters={
                    <Group grow align="flex-end">
                        <Select
                            label={t('fields.organization')}
                            placeholder={t('common.all')}
                            clearable
                            searchable
                            data={orgOptions}
                            value={orgFilter}
                            onChange={setOrgFilter}
                        />
                        <Select
                            label={t('fields.type')}
                            placeholder={t('common.all')}
                            clearable
                            data={SUBJECT_TYPE_VALUES.map((subjectType) => ({
                                value: subjectType,
                                label: t(`subjectTypes.${subjectType}`),
                            }))}
                            value={typeFilter}
                            onChange={setTypeFilter}
                        />
                    </Group>
                }
                columns={[
                    {
                        key: 'name',
                        header: t('fields.name'),
                        render: (r) => (
                            <EntityRef
                                title={r.fullName ?? t('entities.subject')}
                                id={r.id}
                                href={`/subjects?id=${r.id}`}
                                hideId
                            />
                        ),
                    },
                    {
                        key: 'phone',
                        header: t('fields.primaryPhone'),
                        render: (r) => r.primaryPhone ?? t('common.empty'),
                    },
                    {
                        key: 'type',
                        header: t('fields.type'),
                        render: (r) =>
                            r.subjectType ? (
                                <Badge variant="light" size="sm">
                                    {t(`subjectTypes.${r.subjectType}`, {
                                        defaultValue: r.subjectType,
                                    })}
                                </Badge>
                            ) : (
                                t('common.empty')
                            ),
                    },
                ]}
            />
            <DetailDrawer
                opened={Boolean(detailSubjectId)}
                onClose={() => {
                    searchParams.delete('id');
                    setSearchParams(searchParams);
                }}
                title={t('detail.subject')}
                subtitle={detailSubjectId ?? undefined}
            >
                {detailSubjectId ? <SubjectDetailPanel subjectId={detailSubjectId} /> : null}
            </DetailDrawer>
        </>
    );
}
